// @@RECEIPTS_v107@@ Receipts & Invoices — drag-and-drop inbox.
// Drop (or pick, or paste) receipts/invoices → Inbox. Admin/manager mark them
// processed → archived; the server deletes processed items after the retention
// window (90 days). Threaded notes on every item.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import ReceiptRequests from './ReceiptRequests'; // @@RECEIPT_REQUESTS_v122@@

const KINDS = [
  { k: 'receipt', label: 'Receipt' },
  { k: 'invoice', label: 'Invoice' },
  { k: 'other', label: 'Other' },
];
const KIND_LABEL = { receipt: 'Receipt', invoice: 'Invoice', other: 'Other' };
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,application/pdf,image/*';
const MAX_BYTES_DEFAULT = 20 * 1024 * 1024;

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return Math.round(n / 1024) + ' KB';
  return (n / 1048576).toFixed(1) + ' MB';
}
function stamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return ''; }
}
function day(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch (e) { return ''; }
}
async function errText(r, fallback) {
  const j = await r.json().catch(() => ({}));
  if (j.code === 'migration_missing') return 'The database migration for receipts has not been applied yet.';
  return j.error || fallback;
}
async function saveBlob(res, filename) {
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function Countdown({ r }) {
  if (r.status !== 'processed' || r.days_left == null) return null;
  const cls = r.days_left <= 7 ? ' is-red' : r.days_left <= 14 ? ' is-warn' : '';
  return (
    <span className={'hq-v107-clock' + cls} title={'Deleted on ' + day(r.purge_after)}>
      {r.days_left === 0 ? 'Deletes today' : 'Deletes in ' + r.days_left + 'd'}
    </span>
  );
}

function FileIcon({ type }) {
  const pdf = type === 'application/pdf';
  return (
    <span className={'hq-v107-ficon' + (pdf ? ' is-pdf' : '')} aria-hidden="true">
      {pdf ? 'PDF' : (
        <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>
      )}
    </span>
  );
}

// ── drop zone + upload queue ───────────────────────────────────────────────
function Uploader({ maxBytes, onUploaded }) {
  const [kind, setKind] = useState('receipt');
  const [over, setOver] = useState(false);
  const [queue, setQueue] = useState([]);
  const inputRef = useRef(null);
  const kindRef = useRef(kind);
  const busyRef = useRef(false);
  const pendingRef = useRef([]);
  kindRef.current = kind;

  const patch = useCallback((key, p) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...p } : x))), []);

  const pump = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    let any = false;
    while (pendingRef.current.length) {
      const job = pendingRef.current.shift();
      patch(job.key, { state: 'uploading' });
      try {
        const r = await apiFetch('/api/receipts?kind=' + encodeURIComponent(job.kind), {
          method: 'POST',
          headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(job.file.name || 'receipt') },
          body: job.file,
        });
        if (r.ok) {
          const j = await r.json();
          patch(job.key, { state: 'done', receipt: j.receipt, duplicates: j.duplicates || [] });
          any = true;
        } else {
          patch(job.key, { state: 'error', error: await errText(r, 'Upload failed.') });
        }
      } catch (e) { patch(job.key, { state: 'error', error: 'Could not reach the server.' }); }
    }
    busyRef.current = false;
    if (any) onUploaded();
  }, [onUploaded, patch]);

  const addFiles = useCallback((files) => {
    const list = Array.from(files || []).filter((f) => f && f.size !== undefined);
    if (!list.length) return;
    const jobs = list.map((file, i) => ({
      key: Date.now() + '-' + i + '-' + Math.random().toString(36).slice(2, 7),
      file, kind: kindRef.current, name: file.name || 'Pasted image', size: file.size,
      state: file.size > maxBytes ? 'error' : 'queued',
      error: file.size > maxBytes ? 'Larger than ' + Math.round(maxBytes / 1048576) + ' MB.' : '',
    }));
    setQueue((q) => [...jobs, ...q].slice(0, 50));
    pendingRef.current.push(...jobs.filter((j) => j.state === 'queued'));
    pump();
  }, [maxBytes, pump]);

  // Paste a screenshot / copied file anywhere on the page (text pastes are untouched).
  useEffect(() => {
    function onPaste(e) {
      const files = e.clipboardData && e.clipboardData.files;
      if (files && files.length) { e.preventDefault(); addFiles(files); }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles]);

  const active = queue.filter((q) => q.state === 'queued' || q.state === 'uploading').length;
  return (
    <div className="hq-panel hq-v107-upload">
      <div
        className={'hq-v107-drop' + (over ? ' is-over' : '')}
        role="button" tabIndex={0}
        onClick={() => inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
        onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!over) setOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
      >
        <svg className="hq-v107-drop-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>
        <div className="hq-v107-drop-main">{over ? 'Drop to upload' : 'Drop receipts & invoices here'}</div>
        <div className="hq-v107-drop-sub">or click to choose · paste a screenshot · PDF, JPG, PNG, HEIC · up to {Math.round(maxBytes / 1048576)} MB each</div>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} hidden
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>
      <div className="hq-v107-upbar">
        <span className="hq-v107-k">Upload as</span>
        <div className="hq-v101-seg">
          {KINDS.map((k) => (
            <button key={k.k} type="button" className={kind === k.k ? 'on' : ''} onClick={() => setKind(k.k)}>{k.label}</button>
          ))}
        </div>
        {active > 0 && <span className="hq-v107-muted">Uploading {active}…</span>}
        {queue.length > 0 && active === 0 && (
          <button type="button" className="hq-v107-link" onClick={() => setQueue([])}>Clear list</button>
        )}
      </div>
      {queue.length > 0 && (
        <ul className="hq-v107-queue">
          {queue.map((q) => (
            <li key={q.key} className={'is-' + q.state}>
              <span className="hq-v107-qstate" aria-hidden="true">
                {q.state === 'done' ? '✓' : q.state === 'error' ? '!' : q.state === 'uploading' ? <span className="hq-v107-spin" /> : '·'}
              </span>
              <span className="hq-v107-qname">{q.name}</span>
              <span className="hq-v107-muted hq-v107-small">{fmtBytes(q.size)}</span>
              {q.state === 'done' && q.receipt && <span className="hq-v107-muted hq-v107-small">{q.receipt.code} · {KIND_LABEL[q.kind]}</span>}
              {q.state === 'error' && <span className="hq-v107-qerr">{q.error}</span>}
              {q.state === 'done' && q.duplicates && q.duplicates.length > 0 && (
                <span className="hq-v107-qdup">Possible duplicate of {q.duplicates.map((d) => d.code).join(', ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── detail modal ───────────────────────────────────────────────────────────
function Preview({ r }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!r.previewable) return undefined;
    let on = true; let made = null;
    apiFetch('/api/receipts/' + r.id + '/file?inline=1')
      .then(async (res) => {
        if (!res.ok) { if (on) setErr(res.status === 404 ? 'The file is missing from storage.' : 'Preview unavailable.'); return; }
        const b = await res.blob();
        made = URL.createObjectURL(b);
        if (on) setUrl(made); else URL.revokeObjectURL(made);
      })
      .catch(() => { if (on) setErr('Preview unavailable.'); });
    return () => { on = false; if (made) URL.revokeObjectURL(made); };
  }, [r.id, r.previewable]);

  if (!r.previewable) {
    return (
      <div className="hq-v107-noprev">
        <FileIcon type={r.content_type} />
        <p>{r.content_type === 'image/heic' ? 'HEIC photos can’t be previewed in the browser.' : 'No preview for this file type.'}</p>
        <p className="hq-v107-muted hq-v107-small">Use Download to open it.</p>
      </div>
    );
  }
  if (err) return <div className="hq-v107-noprev"><p>{err}</p></div>;
  if (!url) return <div className="hq-v107-noprev"><p className="hq-v107-muted">Loading preview…</p></div>;
  if (r.content_type === 'application/pdf') return <iframe className="hq-v107-pdf" src={url} title={r.title} />;
  return <div className="hq-v107-imgwrap"><img src={url} alt={r.title} /></div>;
}

function DetailModal({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('receipt');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  const apply = useCallback((j) => { setD(j); setTitle(j.receipt.title); setKind(j.receipt.kind); }, []);
  useEffect(() => {
    let on = true;
    setD(null); setErr('');
    apiFetch('/api/receipts/' + id)
      .then(async (r) => {
        if (!on) return;
        if (r.ok) apply(await r.json()); else setErr(r.status === 404 ? 'This receipt no longer exists (it may have been deleted).' : await errText(r, 'Could not load.'));
      })
      .catch(() => { if (on) setErr('Could not reach the server.'); });
    return () => { on = false; };
  }, [id, apply]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [busy, onClose]);

  async function call(label, path, opts) {
    setBusy(label); setErr('');
    try {
      const r = await apiFetch(path, opts);
      if (!r.ok) { setErr(await errText(r, 'Something went wrong.')); return null; }
      const j = await r.json();
      onChanged();
      return j;
    } catch (e) { setErr('Could not reach the server.'); return null; }
    finally { setBusy(''); }
  }
  const json = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

  async function saveMeta() {
    const j = await call('save', '/api/receipts/' + id, json('PATCH', { title, kind }));
    if (j) apply(j);
  }
  async function addNote(e) {
    if (e) e.preventDefault();
    if (!note.trim()) return;
    const j = await call('note', '/api/receipts/' + id + '/notes', json('POST', { note }));
    if (j) { apply(j); setNote(''); }
  }
  async function processIt() {
    const j = await call('process', '/api/receipts/' + id + '/process', json('POST', { note }));
    if (j) { apply(j); setNote(''); }
  }
  async function restore() {
    const j = await call('restore', '/api/receipts/' + id + '/restore', json('POST'));
    if (j) apply(j);
  }
  async function remove() {
    const j = await call('delete', '/api/receipts/' + id, { method: 'DELETE' });
    if (j) onClose();
  }
  async function download() {
    setBusy('dl');
    try {
      const r = await apiFetch('/api/receipts/' + id + '/file');
      if (r.ok) await saveBlob(r, d.receipt.filename); else setErr('Download failed.');
    } catch (e) { setErr('Download failed.'); }
    finally { setBusy(''); }
  }

  const r = d && d.receipt;
  const canEdit = r && (r.status === 'pending' || d.can_manage);
  const dirty = r && (title.trim() !== r.title || kind !== r.kind);
  return (
    <div className="hq-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="hq-modal hq-v107-modal" role="dialog" aria-modal="true" aria-labelledby="v107-title">
        <div className="hq-modal-head">
          <h2 id="v107-title">{r ? <><span className="hq-v107-code">{r.code}</span>{r.title}</> : 'Receipt'}</h2>
          <button type="button" className="hq-modal-x" onClick={onClose} disabled={!!busy} aria-label="Close">×</button>
        </div>
        {!d ? (
          <div className="hq-modal-body">{err ? <p className="hq-v107-err">{err}</p> : <p className="hq-v107-muted">Loading…</p>}</div>
        ) : (
          <div className="hq-v107-split">
            <div className="hq-v107-prev"><Preview r={r} /></div>
            <div className="hq-v107-side">
              <div className="hq-v107-status">
                {r.status === 'pending'
                  ? <span className="hq-pill hq-v107-st-pending">In inbox</span>
                  : <span className="hq-pill hq-v107-st-processed">Processed</span>}
                <Countdown r={r} />
              </div>

              <label className="hq-modal-label" htmlFor="v107-t">Title</label>
              <input id="v107-t" className="hq-input hq-v107-full" value={title} maxLength={140} disabled={!canEdit}
                onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && dirty) saveMeta(); }} />
              <label className="hq-modal-label">Type</label>
              <div className="hq-v107-row">
                <div className="hq-v101-seg">
                  {KINDS.map((k) => (
                    <button key={k.k} type="button" className={kind === k.k ? 'on' : ''} disabled={!canEdit} onClick={() => setKind(k.k)}>{k.label}</button>
                  ))}
                </div>
                {dirty && <button type="button" className="hq-btn hq-v107-sm" onClick={saveMeta} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>}
              </div>

              <dl className="hq-v107-meta">
                <div><dt>File</dt><dd>{r.filename} <span className="hq-v107-muted">· {fmtBytes(r.bytes)}</span></dd></div>
                <div><dt>Uploaded</dt><dd>{stamp(r.created_at)}{r.uploader_name ? ' · ' + r.uploader_name : ''}</dd></div>
                {r.status === 'processed' && <div><dt>Processed</dt><dd>{stamp(r.processed_at)}{r.processor_name ? ' · ' + r.processor_name : ''}</dd></div>}
                {r.status === 'processed' && <div><dt>Deletes on</dt><dd>{day(r.purge_after)}</dd></div>}
              </dl>

              <div className="hq-v107-acts">
                <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={download} disabled={!!busy}>{busy === 'dl' ? 'Downloading…' : 'Download'}</button>
                {d.can_manage && r.status === 'pending' && (
                  <button type="button" className="hq-btn hq-v107-go hq-v107-sm" onClick={processIt} disabled={!!busy}
                    title={note.trim() ? 'Saves your note, then marks processed' : ''}>
                    {busy === 'process' ? 'Saving…' : note.trim() ? 'Add note & mark processed' : 'Mark processed'}
                  </button>
                )}
                {d.can_manage && r.status === 'processed' && (
                  <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={restore} disabled={!!busy}>{busy === 'restore' ? 'Restoring…' : 'Restore to inbox'}</button>
                )}
                {d.can_manage && (confirmDel ? (
                  <span className="hq-v107-confirm">
                    <button type="button" className="hq-btn hq-v107-danger hq-v107-sm" onClick={remove} disabled={!!busy}>{busy === 'delete' ? 'Deleting…' : 'Delete permanently'}</button>
                    <button type="button" className="hq-v107-link" onClick={() => setConfirmDel(false)} disabled={!!busy}>Cancel</button>
                  </span>
                ) : (
                  <button type="button" className="hq-v107-link hq-v107-dellink" onClick={() => setConfirmDel(true)} disabled={!!busy}>Delete</button>
                ))}
              </div>
              {d.can_manage && r.status === 'pending' && (
                <p className="hq-modal-hint">Marking processed archives it. It’s deleted for good {d.retention_days} days later unless restored.</p>
              )}
              {err && <p className="hq-v107-err">{err}</p>}

              <h3 className="hq-v107-h3">Notes{r.note_count ? <span className="hq-v107-muted"> · {r.note_count}</span> : null}</h3>
              <ol className="hq-v107-notes">
                {d.notes.length === 0 && <li className="hq-v107-muted hq-v107-small">No notes yet.</li>}
                {d.notes.map((n) => (
                  <li key={n.id} className={n.kind === 'event' ? 'is-event' : ''}>
                    <div className="hq-v107-nhead"><strong>{n.author || 'Someone'}</strong><span>{stamp(n.created_at)}</span></div>
                    <div className="hq-v107-nbody">{n.body}</div>
                  </li>
                ))}
              </ol>
              <form onSubmit={addNote} className="hq-v107-noteform">
                <textarea className="hq-input hq-v107-textarea" rows={3} maxLength={4000} value={note}
                  placeholder="Add a note — what it was for, job #, card used, entered in Zoho…"
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }} />
                <div className="hq-v107-row hq-v107-row-end">
                  <span className="hq-v107-muted hq-v107-small">⌘/Ctrl + Enter</span>
                  <button type="submit" className="hq-btn hq-btn-ghost hq-v107-sm" disabled={!!busy || !note.trim()}>{busy === 'note' ? 'Adding…' : 'Add note'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────
export default function Receipts() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'processed' ? 'processed' : 'pending';
  const openId = params.get('r');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/receipts?status=' + tab + (q ? '&q=' + encodeURIComponent(q) : ''));
      if (r.ok) { setData(await r.json()); setErr(''); } else setErr(await errText(r, 'Could not load receipts.'));
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setLoading(false); }
  }, [tab, q]);
  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { setSel(new Set()); setConfirmDel(false); }, [tab, q]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: k === 'r' }); // opening/closing a receipt doesn't stack history
  };
  const list = useMemo(() => (data && data.receipts) || [], [data]);
  const canManage = !!(data && data.can_manage);
  const allOn = list.length > 0 && list.every((r) => sel.has(r.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSel(allOn ? new Set() : new Set(list.map((r) => r.id)));

  async function bulk(action) {
    setBusy(action); setFlash('');
    try {
      const r = await apiFetch('/api/receipts/bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ids: [...sel] }),
      });
      if (!r.ok) { setFlash(await errText(r, 'Could not do that.')); return; }
      const j = await r.json();
      const verb = action === 'process' ? 'marked processed' : action === 'restore' ? 'restored to the inbox' : 'deleted';
      setFlash(j.count + ' ' + (j.count === 1 ? 'item' : 'items') + ' ' + verb + '.');
      setSel(new Set()); setConfirmDel(false);
      await load();
    } catch (e) { setFlash('Could not reach the server.'); }
    finally { setBusy(''); }
  }
  async function zip() {
    setBusy('zip'); setFlash('');
    try {
      const r = await apiFetch('/api/receipts/zip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [...sel] }),
      });
      if (!r.ok) { setFlash(await errText(r, 'Could not build the ZIP.')); return; }
      await saveBlob(r, 'XYZ-Receipts-' + new Date().toLocaleDateString('en-CA') + '.zip');
    } catch (e) { setFlash('Could not build the ZIP.'); }
    finally { setBusy(''); }
  }

  const counts = (data && data.counts) || {};
  const soon = tab === 'processed' ? list.filter((r) => r.days_left != null && r.days_left <= 7).length : 0;
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Receipts &amp; Invoices</h1>
        <p>Drop receipts and invoices here to be processed. Processed items are archived and deleted for good {(data && data.retention_days) || 90} days later.</p>
      </div>

      <Uploader maxBytes={(data && data.max_upload_bytes) || MAX_BYTES_DEFAULT} onUploaded={load} />

      <div className="hq-panel">
        <div className="hq-v101-toolbar">
          <div className="hq-v101-seg">
            <button type="button" className={tab === 'pending' ? 'on' : ''} onClick={() => setParam('tab', null)}>
              Inbox{counts.pending != null ? ' (' + counts.pending + ')' : ''}
            </button>
            <button type="button" className={tab === 'processed' ? 'on' : ''} onClick={() => setParam('tab', 'processed')}>
              Processed{counts.processed != null ? ' (' + counts.processed + ')' : ''}
            </button>
          </div>
          <input className="hq-input hq-v107-search" type="search" placeholder="Search title, file, R-#, notes…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {soon > 0 && <p className="hq-v107-warn">{soon} processed {soon === 1 ? 'item is' : 'items are'} deleted within 7 days. Download a ZIP first if you need to keep a copy.</p>}
        {flash && (
          <div className="hq-v107-flash"><span>{flash}</span>
            <button type="button" className="hq-modal-x" onClick={() => setFlash('')} aria-label="Dismiss">×</button>
          </div>
        )}
        {sel.size > 0 && (
          <div className="hq-v107-bulk">
            <span><strong>{sel.size}</strong> selected</span>
            <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={zip} disabled={!!busy}>{busy === 'zip' ? 'Zipping…' : 'Download ZIP'}</button>
            {canManage && tab === 'pending' && (
              <button type="button" className="hq-btn hq-v107-go hq-v107-sm" onClick={() => bulk('process')} disabled={!!busy}>{busy === 'process' ? 'Saving…' : 'Mark processed'}</button>
            )}
            {canManage && tab === 'processed' && (
              <button type="button" className="hq-btn hq-btn-ghost hq-v107-sm" onClick={() => bulk('restore')} disabled={!!busy}>{busy === 'restore' ? 'Restoring…' : 'Restore to inbox'}</button>
            )}
            {canManage && (confirmDel ? (
              <span className="hq-v107-confirm">
                <button type="button" className="hq-btn hq-v107-danger hq-v107-sm" onClick={() => bulk('delete')} disabled={!!busy}>{busy === 'delete' ? 'Deleting…' : 'Delete ' + sel.size + ' permanently'}</button>
                <button type="button" className="hq-v107-link" onClick={() => setConfirmDel(false)}>Cancel</button>
              </span>
            ) : (
              <button type="button" className="hq-v107-link hq-v107-dellink" onClick={() => setConfirmDel(true)} disabled={!!busy}>Delete</button>
            ))}
            <button type="button" className="hq-v107-link" onClick={() => setSel(new Set())}>Clear</button>
          </div>
        )}

        {loading && !data ? (
          <p className="hq-empty">Loading…</p>
        ) : err ? (
          <p className="hq-empty">{err}</p>
        ) : (
          <div className="hq-v107-scroll">
            <table className="hq-table hq-v107-table">
              <thead>
                <tr>
                  <th className="hq-v107-ck"><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Select all" disabled={!list.length} /></th>
                  <th>Item</th>
                  <th className="hq-v107-hide-sm">Type</th>
                  <th className="hq-v107-hide-sm">Uploaded</th>
                  {tab === 'processed' && <th className="hq-v107-hide-md">Processed</th>}
                  <th className="hq-v107-hide-sm">Notes</th>
                  {tab === 'processed' && <th className="r">Deletes</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className={'hq-v107-tr' + (sel.has(r.id) ? ' is-sel' : '')} onClick={() => setParam('r', r.id)}>
                    <td className="hq-v107-ck" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} aria-label={'Select ' + r.code} />
                    </td>
                    <td>
                      <div className="hq-v107-item">
                        <FileIcon type={r.content_type} />
                        <div className="hq-v107-itemtxt">
                          <div className="hq-v107-title"><span className="hq-v107-code">{r.code}</span>{r.title}</div>
                          <div className="hq-v107-muted hq-v107-small hq-v107-ellip">{r.filename} · {fmtBytes(r.bytes)}</div>
                          {r.last_note && <div className="hq-v107-lastnote hq-v107-ellip">“{r.last_note}”</div>}
                        </div>
                      </div>
                    </td>
                    <td className="hq-v107-hide-sm"><span className={'hq-pill hq-v107-k-' + r.kind}>{r.kind_label}</span></td>
                    <td className="hq-v107-hide-sm hq-v107-when">
                      <div>{stamp(r.created_at)}</div>
                      {r.uploader_name && <div className="hq-v107-muted hq-v107-small">{r.uploader_name}</div>}
                    </td>
                    {tab === 'processed' && (
                      <td className="hq-v107-hide-md hq-v107-when">
                        <div>{stamp(r.processed_at)}</div>
                        {r.processor_name && <div className="hq-v107-muted hq-v107-small">{r.processor_name}</div>}
                      </td>
                    )}
                    <td className="hq-v107-hide-sm">{r.note_count ? <span className="hq-v107-ncount">{r.note_count}</span> : <span className="hq-v107-muted">—</span>}</td>
                    {tab === 'processed' && <td className="r"><Countdown r={r} /></td>}
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td className="hq-empty" colSpan={tab === 'processed' ? 7 : 5}>
                    {q ? 'Nothing matches.' : tab === 'pending' ? 'Inbox is empty — drop receipts above.' : 'Nothing processed yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {data && data.truncated && <p className="hq-v107-warn">Showing the newest 500 — use search to narrow it down.</p>}
      </div>

      <ReceiptRequests /> {/* @@RECEIPT_REQUESTS_v122@@ @@RECEIPTS_LAYOUT_v122_2@@ "Looking for" list (printable), under the inbox */}

      {openId && <DetailModal id={openId} onClose={() => setParam('r', null)} onChanged={load} />}
    </div>
  );
}
